import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Gamepad2, Swords, Users, Star, Sparkles } from "lucide-react";

// Featured games to highlight (showing variety of the library)
const featuredGames = [
  { id: "saints-vs-heresies", icon: "⚔️", route: "/saints-vs-heresies" },
  { id: "emoji-quiz", icon: "🎯", route: "/emoji-quiz" },
  { id: "rosary-art", icon: "🎨", route: "/rosary-art-mystery" },
  { id: "finding-jesus", icon: "🗺️", route: "/finding-jesus" },
  { id: "stained-glass", icon: "🪟", route: "/stained-glass-puzzle" },
  { id: "catholic-decoder", icon: "🔣", route: "/catholic-decoder" },
];

export const PilgrimsArcadeShowcase = () => {
  const { t } = useTranslation();

  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-amber-900/10 via-primary/5 to-orange-900/10 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 bg-[url('/lovable-uploads/parchment-texture.png')] opacity-10"></div>
      <div className="absolute top-0 left-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-4 shadow-lg">
            <Sparkles size={16} />
            {t("pilgrimsArcade.freeLabel", "100% FREE")}
          </div>
          <h2 className="font-medieval text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("pilgrimsArcade.title", "Pilgrim's Arcade")}
          </h2>
          <p className="font-quest text-xl md:text-2xl text-primary font-semibold mb-3">
            {t("pilgrimsArcade.subtitle", "18 Free Catholic Games & Activities")}
          </p>
          <p className="font-quest text-lg text-muted-foreground max-w-3xl mx-auto">
            {t("pilgrimsArcade.tagline", "From memory games for toddlers to strategic card battles for teens — entertainment that deepens faith.")}
          </p>
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-8 items-start">
          
          {/* Left: Featured Game Spotlight - Saints vs Heresies */}
          <div className="quest-card p-6 md:p-8 bg-gradient-to-br from-card via-card to-amber-900/5 border-2 border-amber-500/30 shadow-xl group hover:shadow-2xl transition-all duration-300">
            <div className="flex items-center gap-2 mb-4">
              <Star className="text-amber-500" size={20} />
              <span className="font-quest text-sm font-bold text-amber-600 uppercase tracking-wide">
                {t("pilgrimsArcade.featuredGame", "Featured Game")}
              </span>
            </div>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Swords className="text-white" size={32} />
              </div>
              <div>
                <h3 className="font-medieval text-2xl font-bold text-foreground">
                  {t("pilgrimsArcade.saintsVsHeresies.title", "Saints vs Heresies")}
                </h3>
                <p className="font-quest text-sm text-muted-foreground">
                  {t("pilgrimsArcade.saintsVsHeresies.age", "Ages 12+")}
                </p>
              </div>
            </div>
            
            <p className="font-quest text-muted-foreground mb-6 leading-relaxed">
              {t("pilgrimsArcade.saintsVsHeresies.desc", "Command 60 Saints across 5 classes in this strategic card game. Roll dice, defeat heresies, and learn Church history through immersive gameplay!")}
            </p>
            
            <Button asChild size="lg" className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold">
              <Link to="/saints-vs-heresies" className="flex items-center justify-center gap-2">
                <Swords size={20} />
                {t("pilgrimsArcade.playNow", "Play Now")}
              </Link>
            </Button>
          </div>

          {/* Right: Game Grid + Stats */}
          <div className="space-y-6">
            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="quest-card p-4 text-center bg-card/80">
                <div className="font-medieval text-2xl md:text-3xl font-bold text-primary">18</div>
                <div className="font-quest text-xs md:text-sm text-muted-foreground">
                  {t("pilgrimsArcade.stats.games", "Games")}
                </div>
              </div>
              <div className="quest-card p-4 text-center bg-card/80">
                <div className="font-medieval text-2xl md:text-3xl font-bold text-primary">4+</div>
                <div className="font-quest text-xs md:text-sm text-muted-foreground">
                  {t("pilgrimsArcade.stats.ages", "Ages")}
                </div>
              </div>
              <div className="quest-card p-4 text-center bg-card/80">
                <div className="font-medieval text-2xl md:text-3xl font-bold text-primary">∞</div>
                <div className="font-quest text-xs md:text-sm text-muted-foreground">
                  {t("pilgrimsArcade.stats.fun", "Fun")}
                </div>
              </div>
            </div>

            {/* Popular games grid */}
            <div className="quest-card p-6 bg-card/80">
              <h4 className="font-medieval text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Users size={18} className="text-primary" />
                {t("pilgrimsArcade.popularGames", "Popular Games")}
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {featuredGames.map((game) => (
                  <Link
                    key={game.id}
                    to={game.route}
                    className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 hover:bg-primary/10 border border-border hover:border-primary/30 transition-all group"
                  >
                    <span className="text-2xl">{game.icon}</span>
                    <span className="font-quest text-sm text-foreground group-hover:text-primary transition-colors truncate">
                      {t(`pilgrimsArcade.games.${game.id}`, game.id.replace("-", " "))}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Main CTA */}
            <Button asChild size="lg" className="w-full quest-button text-lg py-6 h-auto">
              <Link to="/pilgrims-arcade" className="flex items-center justify-center gap-3">
                <Gamepad2 size={24} />
                {t("pilgrimsArcade.enterArcade", "Enter the Arcade")}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PilgrimsArcadeShowcase;
